import React, { useState } from 'react';
import {
    Drawer, Box, Typography, Stack, IconButton, Button, FormControl,
    FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, TextField,
    InputAdornment, Tooltip, Divider, Badge,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const WEIGHTING_OPTIONS = [
    { value: 'equal', label: 'Equal' },
    { value: 'students', label: 'Students' },
    { value: 'jobs', label: 'Jobs' },
    { value: 'providers', label: 'Providers' },
    { value: 'composite', label: 'Composite' },
];

const FAULT_OPTIONS = [
    { value: 'all', label: 'All failures' },
    { value: 'ours', label: 'Only our-fault' },
    { value: 'theirs', label: 'Only their-fault' },
];

const ChecklistColumn = ({ title, items, excludedSet, onToggle, onAll, onNone }) => {
    const [q, setQ] = useState('');
    const filtered = q.trim()
        ? items.map((n, i) => ({ n, i })).filter(({ n }) => n.toLowerCase().includes(q.toLowerCase()))
        : items.map((n, i) => ({ n, i }));
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{title}</Typography>
                <Box>
                    <Button size="small" onClick={onAll} sx={{ minWidth: 0, p: '2px 6px', fontSize: 11 }}>all</Button>
                    <Button size="small" onClick={onNone} sx={{ minWidth: 0, p: '2px 6px', fontSize: 11 }}>none</Button>
                </Box>
            </Stack>
            <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="filter…"
                size="small"
                fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 12, py: 0.75 } }}
            />
            <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 0.5 }}>
                {filtered.map(({ n, i }) => {
                    const excluded = excludedSet.has(i);
                    return (
                        <FormControlLabel
                            key={i}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!excluded}
                                    onChange={() => onToggle(i)}
                                    sx={{ p: 0.5 }}
                                />
                            }
                            label={
                                <span style={{
                                    fontSize: 12,
                                    textDecoration: excluded ? 'line-through' : 'none',
                                    color: excluded ? '#aaa' : 'inherit',
                                }}>{n}</span>
                            }
                            sx={{ display: 'flex', m: 0, py: 0.25 }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

export const FilterToggle = ({ open, onClick, excludedCount }) => (
    <Tooltip title="Filter & weighting">
        <IconButton onClick={onClick} color={open ? 'primary' : 'default'} sx={{ position: 'relative' }}>
            <Badge color="error" badgeContent={excludedCount || null}>
                <FilterAltIcon />
            </Badge>
        </IconButton>
    </Tooltip>
);

const FilterDialog = ({ open, onClose, data, state, dispatch }) => {
    const totalExcluded = state.excludedClients.size + state.excludedProviders.size;

    const toggleClient = (i) => dispatch({ type: 'toggle-client', i });
    const toggleProvider = (i) => dispatch({ type: 'toggle-provider', i });
    const setAllClients = (exclude) => dispatch({ type: 'set-all-clients', exclude });
    const setAllProviders = (exclude) => dispatch({ type: 'set-all-providers', exclude });

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2.5 } }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Filter & weighting</Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Stack>

            <FormControl sx={{ mb: 2 }}>
                <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Weight clients by</FormLabel>
                <RadioGroup row value={state.weighting} onChange={(e) => dispatch({ type: 'weighting', value: e.target.value })}>
                    {WEIGHTING_OPTIONS.map((o) => (
                        <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={<span style={{ fontSize: 12 }}>{o.label}</span>} />
                    ))}
                </RadioGroup>
                <Typography sx={{ fontSize: 11, color: '#888' }}>
                    Affects <b>overall</b> provider-run and client-night rates (TL;DR + trend chart). Per-entity ranking tables are unaffected. Composite = geometric mean of the three factors.
                </Typography>
            </FormControl>

            <Divider />

            <FormControl sx={{ mt: 2, mb: 2 }}>
                <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Count as failure</FormLabel>
                <RadioGroup row value={state.faultMode} onChange={(e) => dispatch({ type: 'fault-mode', value: e.target.value })}>
                    {FAULT_OPTIONS.map((o) => (
                        <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={<span style={{ fontSize: 12 }}>{o.label}</span>} />
                    ))}
                </RadioGroup>
                <Typography sx={{ fontSize: 11, color: '#888' }}>
                    "Only ours" treats external failures (auth, 5xx, timeouts, missing source files) as successes — isolates engineering effectiveness. "Only theirs" does the inverse.
                </Typography>
            </FormControl>

            <Divider sx={{ mb: 2 }} />

            <Stack direction="row" spacing={2}>
                <ChecklistColumn
                    title="Clients"
                    items={data.clients}
                    excludedSet={state.excludedClients}
                    onToggle={toggleClient}
                    onAll={() => setAllClients(false)}
                    onNone={() => setAllClients(true)}
                />
                <ChecklistColumn
                    title="Providers"
                    items={data.providers}
                    excludedSet={state.excludedProviders}
                    onToggle={toggleProvider}
                    onAll={() => setAllProviders(false)}
                    onNone={() => setAllProviders(true)}
                />
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                <Button onClick={() => dispatch({ type: 'reset' })} variant="outlined" size="small">Reset all</Button>
                <Typography sx={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>
                    {totalExcluded ? `${totalExcluded} excluded` : 'no exclusions'}
                </Typography>
            </Stack>
        </Drawer>
    );
};

export default FilterDialog;
